import React, { useState, useEffect } from 'react';
import { useFarm } from '../../contexts/FarmContext';
import { supabase } from '../../config/supabase';
import OrganizationSelector from '../../components/OrganizationSelector';
import FarmSelector from '../../components/FarmSelector';
import {
  FunnelIcon,
  ArrowsRightLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import FarmSearchDropdown from '../../components/FarmSearchDropdown';

const Transaction = () => {
  const { farms, isAdmin, hasOrganizations, hasFarms, getActiveFarmIds, loading: farmLoading } = useFarm();
  const [transactions, setTransactions] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarmFilter, setSelectedFarmFilter] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalIncome: 0,
    totalExpense: 0,
    netBalance: 0
  });
  const [dateRange, setDateRange] = useState({
    start: '2025-01-01',
    end: new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split('T')[0]
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Get active farm IDs from context (filtered by organization)
  const activeFarmIds = getActiveFarmIds();
  const activeFarmIdsKey = JSON.stringify(activeFarmIds);

  // Fetch ALL data for stats (using pagination to bypass limits)
  const fetchAllData = async (farmIds) => {
    if (!farmIds || farmIds.length === 0) {
      console.log('💳 Transaction: No active farms, returning empty');
      return [];
    }

    console.log('💳 Transaction: Fetching data for', farmIds.length, 'farms');
    let allRecords = [];
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      let query = supabase
        .from('financial_transaction_view')
        .select('id, date_transaction, amount, in, description, farm_name, farm, fin_name_type, produce_name, mtnm_produce_name, name_material, fin_subtype_name')
        .in('farm', farmIds)
        .order('date_transaction', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (dateRange.start) {
        query = query.gte('date_transaction', dateRange.start);
      }
      if (dateRange.end) {
        query = query.lte('date_transaction', dateRange.end);
      }
      if (selectedFarmFilter !== 'all') {
        query = query.eq('farm', parseInt(selectedFarmFilter));
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching data:', error);
        break;
      }

      if (data && data.length > 0) {
        allRecords = [...allRecords, ...data];
        hasMore = data.length === pageSize;
        page++;
      } else {
        hasMore = false;
      }
    }

    console.log('💳 Transaction: Loaded', allRecords.length, 'records');
    return allRecords;
  };

  // Load transaction data
  useEffect(() => {
    const loadTransactions = async () => {
      if (farmLoading) {
        console.log('💳 Transaction: Waiting for farms to load...');
        return;
      }

      setLoading(true);
      setCurrentPage(1);
      try {
        // Fetch ALL data for accurate stats, filtered by active farm IDs
        const data = await fetchAllData(activeFarmIds);

        // Transform data
        let allTransactions = (data || []).map(t => ({
          id: t.id,
          date: t.date_transaction,
          type: t.in ? 'income' : 'expense',
          description: t.produce_name || t.mtnm_produce_name || t.name_material || t.fin_subtype_name || t.description || t.fin_name_type || '-',
          amount: parseFloat(t.amount) || 0,
          farm_name: t.farm_name
        }));

        // Filter by type if selected
        if (selectedType === 'income') {
          allTransactions = allTransactions.filter(t => t.type === 'income');
        } else if (selectedType === 'expense') {
          allTransactions = allTransactions.filter(t => t.type === 'expense');
        }

        setAllData(allTransactions);
        setTransactions(allTransactions.slice(0, itemsPerPage));

        // Calculate stats from ALL data (before type filter for accurate totals)
        const incomeData = (data || []).filter(t => t.in === true);
        const expenseData = (data || []).filter(t => t.in === false);
        const totalIncome = incomeData.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
        const totalExpense = expenseData.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

        setStats({
          totalTransactions: allTransactions.length,
          totalIncome,
          totalExpense,
          netBalance: totalIncome - totalExpense
        });
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFarmFilter, selectedType, dateRange, activeFarmIdsKey, farmLoading]);

  // Handle pagination
  const totalPages = Math.ceil(allData.length / itemsPerPage);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
    const startIndex = (page - 1) * itemsPerPage;
    setTransactions(allData.slice(startIndex, startIndex + itemsPerPage));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'RM0.00';
    return `RM${Math.abs(parseFloat(value)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transaction</h1>
        </div>
        <div className="flex items-center space-x-4">
          {hasOrganizations && <OrganizationSelector />}
          {hasFarms && <FarmSelector />}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600">View all financial transactions across farms</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <FarmSearchDropdown
              farms={farms}
              selectedFarmId={selectedFarmFilter}
              onSelect={setSelectedFarmFilter}
              label="Farm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Transactions</option>
              <option value="income">Income Only</option>
              <option value="expense">Expense Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalTransactions}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <ArrowsRightLeftIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Income</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalIncome)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <ArrowUpIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Expense</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalExpense)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <ArrowDownIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Net Balance</p>
              <p className={`text-2xl font-bold ${stats.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.netBalance >= 0 ? '' : '-'}{formatCurrency(stats.netBalance)}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${stats.netBalance >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <BanknotesIcon className={`h-6 w-6 ${stats.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-3 text-gray-600">Loading transactions...</span>
          </div>
        ) : transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(txn.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        txn.type === 'income' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {txn.type === 'income' ? (
                          <ArrowUpIcon className="h-3 w-3 mr-1" />
                        ) : (
                          <ArrowDownIcon className="h-3 w-3 mr-1" />
                        )}
                        {txn.type === 'income' ? 'Income' : 'Expense'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {txn.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {txn.farm_name || '-'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      txn.type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <ArrowsRightLeftIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No transactions found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or date range</p>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, allData.length)} of {allData.length} transactions
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm border rounded-lg ${
                      currentPage === pageNum
                        ? 'bg-green-600 text-white border-green-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transaction;
